import { getApp, getApps, initializeApp } from "firebase/app";
import { Auth, connectAuthEmulator, getAuth } from "firebase/auth";
import { Firestore, getFirestore } from "firebase/firestore";

import { isServedOverNgrok } from "@/lib/get-base-url";
import {
  getRuntimeFirebaseAuthEmulatorHost,
  isPreview,
} from "@/lib/runtime-env";

const firebaseConfig = {
  apiKey: "AIzaSyAcJo2m5es8ZbUn3T9lsL4H--1rfd7tJRM",
  authDomain: "decoda-397301.firebaseapp.com",
  databaseURL: "https://decoda-397301-default-rtdb.firebaseio.com",
  projectId: "decoda-397301",
  storageBucket: "decoda-397301.appspot.com",
  messagingSenderId: "1015834924075",
  appId: "1:1015834924075:web:a9232df89e544a3e73c66e",
  database: "decoda-messaging",
};

/**
 * Loopback, a private (RFC1918) LAN address, or a .local mDNS name: reachable
 * without leaving the network, so the Auth emulator there is plain http.
 * Mirrors `isLocalEmulatorHost` in the mobile app.
 */
function isLocalEmulatorHost(hostPort: string): boolean {
  const host = hostPort.split(":")[0] ?? "";
  if (host === "localhost" || host.endsWith(".local")) return true;
  if (host === "0.0.0.0" || host.startsWith("127.")) return true;
  if (host.startsWith("10.") || host.startsWith("192.168.")) return true;
  const m = /^172\.(\d{1,2})\./.exec(host);
  return m ? Number(m[1]) >= 16 && Number(m[1]) <= 31 : false;
}

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const preview = isPreview();
// Over ngrok the browser can't reach the emulator on localhost, so it uses the
// emulator's own tunnel (`NGROK_AUTH_EMULATOR=1 pnpm dev:ngrok`).
const ngrokEmulatorHost = process.env.NEXT_PUBLIC_NGROK_AUTH_EMULATOR_HOST;
const emulatorHost =
  ngrokEmulatorHost && isServedOverNgrok()
    ? ngrokEmulatorHost.replace(/^https?:\/\//, "").replace(/\/$/, "")
    : getRuntimeFirebaseAuthEmulatorHost();

/**
 * A preview must never touch the production Firebase project. The Auth SDK
 * silently falls back to prod `decoda-397301` if no emulator is connected, so
 * in preview mode we require an emulator host and fail loudly otherwise.
 */
if (preview && !emulatorHost) {
  throw new Error(
    "Preview context detected but no Firebase Auth emulator host is configured " +
      "in window.__ENV / NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST. Refusing to " +
      "fall back to the production Firebase project. Check the preview's " +
      "/runtime-env.js.",
  );
}

const auth: Auth = getAuth(app);

if (
  emulatorHost &&
  !(auth as unknown as { emulatorConfig?: unknown }).emulatorConfig
) {
  // The emulator itself never serves TLS, so loopback and private-LAN hosts
  // speak plain HTTP; proxied preview hosts (e.g. :443) need https. A private
  // LAN address is how a phone reaches the emulator on a dev Mac (the mobile
  // app's embedded console), and https there failed the sign-in handoff.
  const protocol = isLocalEmulatorHost(emulatorHost) ? "http" : "https";
  connectAuthEmulator(auth, `${protocol}://${emulatorHost}`, {
    disableWarnings: true,
  });
}

/**
 * Gate Firestore so a preview never constructs the prod `decoda-397301`
 * Firestore client. Firestore has no emulator in preview, so accessing it in
 * preview mode is a bug — surface it rather than silently hitting prod.
 */
let dbInstance: Firestore | null = null;
function getDb(): Firestore {
  if (preview) {
    throw new Error(
      "Firestore is not available in preview mode (no emulator configured). " +
        "Refusing to construct the production Firestore client.",
    );
  }
  if (!dbInstance) {
    dbInstance = getFirestore();
  }
  return dbInstance;
}

const db = new Proxy({} as Firestore, {
  get(_target, prop, receiver) {
    const value = Reflect.get(getDb(), prop, receiver);
    return typeof value === "function" ? value.bind(getDb()) : value;
  },
  getPrototypeOf() {
    return Reflect.getPrototypeOf(getDb());
  },
});

export { app, db, getDb, auth };
